import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Target, CheckSquare, CalendarDays,
  BarChart3, ShieldAlert, Trophy, Settings, Menu, Mail
} from 'lucide-react';
import { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';

const navItems = [
  { path: '/',             label: 'Dashboard',          icon: LayoutDashboard },
  { path: '/vicios',       label: 'Hábitos para reduzir', icon: Target },
  { path: '/habitos',      label: 'Hábitos',             icon: CheckSquare },
  { path: '/calendario',   label: 'Calendário',          icon: CalendarDays },
  { path: '/estatisticas', label: 'Estatísticas',        icon: BarChart3 },
  { path: '/sos',          label: 'SOS',                 icon: ShieldAlert },
  { path: '/conquistas',   label: 'Conquistas',          icon: Trophy },
  { path: '/configuracoes',label: 'Configurações',       icon: Settings },
];

const mobileNavItems = [
  { path: '/',             label: 'Home',       icon: LayoutDashboard },
  { path: '/habitos',      label: 'Hábitos',    icon: CheckSquare },
  { path: '/sos',          label: 'SOS',        icon: ShieldAlert },
  { path: '/conquistas',   label: 'Conquistas', icon: Trophy },
  { path: '/configuracoes',label: 'Mais',       icon: Menu },
];

const OVERFLOW_PATHS = ['/configuracoes', '/vicios', '/calendario', '/estatisticas'];

function SidebarNav({ onClose }) {
  const { user } = useAuth();
  const location = useLocation();

  return (
    <div className="flex flex-col h-full" data-testid="sidebar-nav">
      <div className="px-6 py-6">
        <h1 className="text-2xl font-bold text-primary tracking-tight"
          style={{ fontFamily: "'DM Serif Display', serif" }}>VIRTU</h1>
        <p className="text-xs text-muted-foreground mt-1">Sua jornada de transformação</p>
      </div>

      <Separator className="opacity-30" />

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto"
        style={{ overscrollBehavior: 'contain' }}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <NavLink key={item.path} to={item.path} onClick={onClose}
              data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              className={cn(
                'flex items-center gap-3 px-4 min-h-[44px] rounded-xl',
                'text-[13px] font-medium transition-colors duration-150 touch-manipulation select-none',
                isActive
                  ? 'bg-primary/15 text-primary'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground active:bg-muted'
              )}
            >
              <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={isActive ? 2.5 : 1.8} />
              <span className="truncate">{item.label}</span>
              {isActive && <span className="ml-auto w-1 h-4 rounded-full bg-primary" aria-hidden />}
            </NavLink>
          );
        })}
      </nav>

      <Separator className="opacity-30" />

      <div className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center
            text-primary font-semibold text-sm flex-shrink-0">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium truncate">{user?.name || 'Usuário'}</p>
            <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="px-5 pb-5"
        style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}>
        <div className="text-[10px] text-muted-foreground/50 space-y-1">
          <div className="flex items-center gap-1.5">
            <Mail className="w-3 h-3" />
            <span>suporte@appvirtu.com.br</span>
          </div>
          <p>&copy; 2026 VIRTU — Todos os direitos reservados</p>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-background flex">

      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0
        border-r border-border/40 bg-card/50 backdrop-blur-xl z-30"
        style={{ willChange: 'transform' }}>
        <SidebarNav />
      </aside>

      <header className="lg:hidden fixed top-0 inset-x-0 z-40
        bg-card/75 backdrop-blur-xl border-b border-border/40"
        style={{ paddingTop: 'env(safe-area-inset-top)', willChange: 'transform' }}>
        <div className="flex items-center justify-between px-4 h-14">
          <h1 className="text-lg font-bold text-primary tracking-tight"
            style={{ fontFamily: "'DM Serif Display', serif" }}>VIRTU</h1>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="w-11 h-11 touch-manipulation"
                data-testid="mobile-menu-btn" aria-label="Abrir menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 bg-card/90 backdrop-blur-2xl border-border/40">
              <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
              <SidebarNav onClose={close} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1 lg:ml-64 min-w-0" style={{ overscrollBehavior: 'contain' }}>
        <div className="pt-14 lg:pt-0 pb-20 lg:pb-8"
          style={{
            paddingTop:    'calc(3.5rem + env(safe-area-inset-top))',
            paddingBottom: 'max(5rem, calc(4rem + env(safe-area-inset-bottom)))',
          }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            <Outlet />
          </div>
        </div>
      </main>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40
        bg-card/85 backdrop-blur-xl border-t border-border/40"
        style={{ willChange: 'transform' }}
        data-testid="mobile-bottom-nav" aria-label="Navegação principal">
        <div className="flex items-center justify-around h-16 px-1"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === '/configuracoes'
              ? OVERFLOW_PATHS.includes(location.pathname)
              : location.pathname === item.path;
            return (
              <NavLink key={item.path} to={item.path}
                data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5',
                  'min-w-[44px] min-h-[44px] px-2 rounded-xl relative',
                  'transition-colors duration-150 touch-manipulation select-none',
                  isActive ? 'text-primary' : 'text-muted-foreground active:text-foreground'
                )}>
                <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 1.6} />
                <span className="text-[10px] font-medium leading-none">{item.label}</span>
                {isActive && <span className="absolute bottom-1.5 w-1 h-1 rounded-full bg-primary" aria-hidden />}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
